import { useCallback, useMemo } from "react";
import {
  Alert,
  Loader,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowsExchange,
  IconReceipt,
} from "@tabler/icons-react";

import type { CasePrefixReassignmentContext } from "../../types/casePrefixReassignment";

export interface SignedCorrectionPrefixSelection {
  targetPrefixId: string;
  caseNumberRemainder: string;
}

export type SignedCorrectionPrefixErrors = Partial<
  Record<keyof SignedCorrectionPrefixSelection, string>
>;

const MAX_CANONICAL_CASE_NUMBER_LENGTH = 10;

interface SignedCorrectionPrefixFieldsProps {
  context?: CasePrefixReassignmentContext;
  isLoading: boolean;
  isError: boolean;
  disabled: boolean;
  selection: SignedCorrectionPrefixSelection;
  errors: SignedCorrectionPrefixErrors;
  onChange: (selection: SignedCorrectionPrefixSelection) => void;
}

const emptySelection: SignedCorrectionPrefixSelection = {
  targetPrefixId: "",
  caseNumberRemainder: "",
};

function getCaseNumberRemainderError(
  selection: SignedCorrectionPrefixSelection,
  context: CasePrefixReassignmentContext
): string | undefined {
  const prefix = context.availablePrefixes.find(
    (option) => option.id === selection.targetPrefixId
  );
  if (!prefix?.requiresCaseNumberRemainder) return undefined;
  const remainder = selection.caseNumberRemainder.trim();
  if (!remainder) return "Enter the case number after the fixed prefix.";
  return prefix.code.length + remainder.length >
    MAX_CANONICAL_CASE_NUMBER_LENGTH
    ? "The full case number must be 10 characters or fewer."
    : undefined;
}

function getTargetSelection(
  context: CasePrefixReassignmentContext,
  targetPrefixId: string
): SignedCorrectionPrefixSelection | undefined {
  const prefix = context.availablePrefixes.find(
    (option) => option.id === targetPrefixId
  );
  if (!prefix) return undefined;
  return {
    targetPrefixId,
    caseNumberRemainder: "",
  };
}

export function validateSignedCorrectionPrefixSelection(
  selection: SignedCorrectionPrefixSelection,
  context?: CasePrefixReassignmentContext
): SignedCorrectionPrefixErrors {
  if (!selection.targetPrefixId) return {};
  if (!context) return { targetPrefixId: "Choose an available prefix." };
  const prefix = context.availablePrefixes.find(
    (option) => option.id === selection.targetPrefixId
  );
  if (!prefix) return { targetPrefixId: "Choose an available prefix." };
  const remainderError = getCaseNumberRemainderError(selection, context);
  return {
    ...(remainderError ? { caseNumberRemainder: remainderError } : {}),
  };
}

export default function SignedCorrectionPrefixFields({
  context,
  isLoading,
  isError,
  disabled,
  selection,
  errors,
  onChange,
}: SignedCorrectionPrefixFieldsProps) {
  const prefixOptions = useMemo(
    () =>
      (context?.availablePrefixes ?? []).map((prefix) => ({
        value: prefix.id,
        label: prefix.code,
      })),
    [context?.availablePrefixes]
  );
  const selectedPrefix = useMemo(
    () =>
      context?.availablePrefixes.find(
        (prefix) => prefix.id === selection.targetPrefixId
      ),
    [context?.availablePrefixes, selection.targetPrefixId]
  );
  const canonicalCaseNumber = useMemo(
    () =>
      selectedPrefix?.requiresCaseNumberRemainder
        ? `${selectedPrefix.code}${selection.caseNumberRemainder
            .trim()
            .toUpperCase()}`
        : "",
    [selectedPrefix, selection.caseNumberRemainder]
  );
  const maximumRemainderLength = useMemo(
    () =>
      selectedPrefix
        ? MAX_CANONICAL_CASE_NUMBER_LENGTH - selectedPrefix.code.length
        : MAX_CANONICAL_CASE_NUMBER_LENGTH,
    [selectedPrefix]
  );
  const billingChangeNotice = useMemo(() => {
    if (!selectedPrefix) return "";
    if (!selectedPrefix.defaultBillingScenarioName) {
      return `${selectedPrefix.code} has no default billing scenario configured, so this case keeps the billing scenario it has now.`;
    }
    return `This case will bill as ${selectedPrefix.defaultBillingScenarioName}, the default for ${selectedPrefix.code}. Any scenario it holds now is replaced.`;
  }, [selectedPrefix]);

  const handleTargetPrefixChange = useCallback(
    (targetPrefixId: string | null) => {
      if (!targetPrefixId) {
        onChange(emptySelection);
        return;
      }
      if (!context) return;
      const nextSelection = getTargetSelection(context, targetPrefixId);
      if (nextSelection) onChange(nextSelection);
    },
    [context, onChange]
  );

  const handleCaseNumberRemainderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({
        ...selection,
        caseNumberRemainder: event.currentTarget.value,
      });
    },
    [onChange, selection]
  );

  return (
    <Stack
      gap="sm"
      p="md"
      style={{
        border: "1px solid var(--mantine-color-default-border)",
        borderRadius: "var(--mantine-radius-sm)",
      }}
    >
      <Stack gap={2}>
        <Title order={5}>Change case prefix (optional)</Title>
        <Text size="sm" c="dimmed">
          Keep this blank to retain the signed case number.
        </Text>
      </Stack>

      {isLoading ? (
        <Loader size="sm" aria-label="Loading signed correction prefixes" />
      ) : isError ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="Unable to load prefix options"
          role="alert"
        >
          Close and reopen this correction before changing the case prefix.
        </Alert>
      ) : context && !context.canReassign ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="orange"
          title="Prefix change unavailable"
          role="status"
        >
          This signed case cannot have its prefix changed in its current state.
        </Alert>
      ) : context ? (
        <>
          <Text size="sm" fw={600}>
            Current number: {context.currentIdentity.caseNumber} (
            {context.currentIdentity.prefix.code})
          </Text>

          <Select
            label="New prefix"
            description="Choose a different active prefix, or leave this blank."
            placeholder="Keep current prefix"
            data={prefixOptions}
            value={selection.targetPrefixId || null}
            onChange={handleTargetPrefixChange}
            error={errors.targetPrefixId}
            clearable
            clearButtonProps={{
              "aria-label": "Clear prefix selection",
              "aria-hidden": false,
            }}
            searchable
            disabled={disabled}
            comboboxProps={{
              position: "bottom-start",
              offset: 8,
              withinPortal: true,
            }}
          />

          {selectedPrefix?.requiresCaseNumberRemainder && (
            <TextInput
              label="New case number"
              description={`Prefix ${selectedPrefix.code} is fixed. Enter the remaining characters.`}
              placeholder="26-12345"
              leftSection={selectedPrefix.code}
              leftSectionWidth={50}
              value={selection.caseNumberRemainder}
              onChange={handleCaseNumberRemainderChange}
              error={errors.caseNumberRemainder}
              required
              maxLength={maximumRemainderLength}
              disabled={disabled}
            />
          )}

          {selectedPrefix?.requiresCaseNumberRemainder ? (
            <Alert
              icon={<IconArrowsExchange size={18} />}
              color="blue"
              title={
                canonicalCaseNumber
                  ? `The new case number will be ${canonicalCaseNumber}`
                  : `Enter the number after ${selectedPrefix.code}`
              }
            >
              The full case number must be unique.
            </Alert>
          ) : selectedPrefix ? (
            <Alert
              icon={<IconArrowsExchange size={18} />}
              color="blue"
              title={`A new ${selectedPrefix.code} number will be generated`}
            >
              The server will assign the lowest safe reusable number first, then
              the next sequence number. The existing internal number will be
              returned to its reusable pool when applicable.
            </Alert>
          ) : null}

          {selectedPrefix && (
            <Alert
              icon={<IconReceipt size={18} />}
              color="blue"
              variant="light"
              title="Billing scenario follows the new prefix"
            >
              {billingChangeNotice}
            </Alert>
          )}

          {selectedPrefix && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="orange"
              title="Printed labels may become stale"
            >
              The server will report which cassette or slide labels need to be
              reprinted after the correction succeeds.
            </Alert>
          )}
        </>
      ) : null}
    </Stack>
  );
}
